'use client';

import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { Image } from '@/app_components/common/Image';
import * as Accordion from '@/app_components/ui/Accordion';
import { Button } from '@/app_components/ui/Button';
import {
  formatTerm,
  type RatesInfoOutput,
  type RatesInfoOutputItem,
  type RatesInfoOutputKey,
  type RatesInfoOutputValueWithItem,
  ratesInfoOutput,
} from '@/app_data/widgets/RedVentures/constants';
import type { RatesData } from '@/app_data/widgets/utils';
import { cn, formatDate } from '@/app_lib/utils/utils';
import { DataPoint } from './DataPoint';
import { RatesSavings } from './RatesSavings';

/** Legacy code (Business logic) - also commented out as it is unused but necessary */
// const appendTTCID = (url: string, postId: number): string => {
//   try {
//     const newUrl = new URL(url);
//     // eslint-disable-next-line no-constant-condition
//     newUrl.searchParams.append('ttcid', `${false ? 'mobile' : 'desktop'}-${postId.toString()}`);
//     return newUrl.toString();
//   } catch {
//     return '';
//   }
// };

function mergeObject(obj1: RatesInfoOutput, obj2: RatesInfoOutputItem): RatesInfoOutputValueWithItem {
  const result = {} as RatesInfoOutputValueWithItem;

  for (const key of Object.keys(obj1) as (keyof RatesInfoOutput)[]) {
    result[key] = {
      ...obj1[key],
      ...obj2[key],
    } as RatesInfoOutputValueWithItem[typeof key];
  }

  return result;
}

interface BankCardProps {
  data: RatesData;
  depositAmount: number;
  outputs: RatesInfoOutputKey[];
  // TODO: Add this back
  // postId: number;
}

export const RatesCard = ({ data, depositAmount, outputs }: BankCardProps) => {
  const { advertiser_notes, effective_at, apy, is_paid, ep_url, min_to_open, product, institution } = data;
  // const newUrl = appendTTCID(ep_url, postId);
  const newUrl = ep_url;
  const router = useRouter();

  const isCd = product.type === 'CD';

  const offerings = useMemo(() => {
    const PercentConversionFactor = 100;

    const offeringsData: RatesInfoOutput = {
      apy: {
        date: formatDate(effective_at),
        dateLabel: 'Effective at date',
        value: apy ? `${apy.toFixed(2)} %` : '',
        valueLabel: 'APY value',
      },
      estEarnings: {
        date: 'Over 1 year',
        dateLabel: 'EST. Earnings value',
        value: `$ ${Math.round(depositAmount * (apy / PercentConversionFactor))}`,
        valueLabel: 'EST. Earnings value',
      },
      featured: {},
      minBalance: {
        value: `$ ${min_to_open}`,
        valueLabel: 'Minimum balance for APY value',
      },
      minDeposit: {
        value: `$ ${min_to_open}`,
        valueLabel: 'Minimum deposit value',
      },
      term: {
        value: formatTerm(product.term || 1),
        valueLabel: 'Term value',
      },
    };

    return mergeObject(offeringsData, ratesInfoOutput);
  }, [apy, depositAmount, effective_at, min_to_open, product.term]);

  const quickLook = (
    <div className='flex flex-col gap-4'>
      <div>
        QUICK LOOK
        <br />
        {institution.reviews.short_summary}
      </div>
      <button
        className={cn(
          'typography-heading-graphik-condensed-level-6 flex items-center text-accent-primary uppercase hover:underline active:underline',
          !institution.reviews.link && 'hidden',
        )}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          router.push(institution.reviews.link || '/');
        }}
        onKeyDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (['Enter', ' '].includes(e.key)) {
            router.push(institution.reviews.link || '/');
          }
        }}
        type='button'
      >
        Read Bank Review <span className='icon icon-arrow-right h-[32px] w-[32px]' />
      </button>
    </div>
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Entire div needs to be clickable based on if the url is set
    // biome-ignore lint/nursery/noNoninteractiveElementInteractions: need event handlers
    <div
      className={cn(
        'relative flex w-full flex-col @2xl:items-start items-center gap-4 self-stretch p-4 ring-accent-primary hover:ring-1',
        newUrl && 'cursor-pointer',
      )}
      onClick={() => {
        if (newUrl) {
          router.push(newUrl);
        }
      }}
      onKeyDown={(e) => {
        if (['Enter', ' '].includes(e.key) && newUrl) {
          e.preventDefault();
          e.stopPropagation();
          router.push(newUrl);
        }
      }}
    >
      {/* Logo, Data Points, Savings */}
      <div className='@2xl:flex contents justify-between gap-4 self-stretch'>
        {/* Logo and Savings on Mobile */}
        <div className='flex @2xl:contents justify-between self-stretch'>
          {/* Logo */}
          <div className='relative flex shrink grow basis-0 leading-0'>
            <span className='flex h-full items-center bg-white'>
              <Image
                alt={institution.name}
                className='w-[128px]'
                height={50}
                pictureClassName='border-4 border-white'
                src={
                  URL.canParse(institution.logo_url)
                    ? institution.logo_url
                    : `https://www.brimg.net/system/img/inst/${institution.logo_url}`
                }
                width={119}
              />
            </span>
          </div>
          {/* Savings */}
          <RatesSavings
            className='@2xl:hidden'
            institution={institution}
            isCd={isCd}
            product={product}
          />
        </div>

        {/* Data Points */}
        <ul className='grid @2xl:contents w-full grid-cols-2 @2xl:gap-4 gap-x-4 gap-y-2 self-stretch'>
          {outputs
            .filter((output) => output !== 'featured')
            .map((output) => {
              const offering = offerings[output];
              return (
                <DataPoint
                  bottomValue={offering.date}
                  className='shrink grow basis-0'
                  key={`bank_card_${offering.title}`}
                  side={offering.side}
                  title={offering.title}
                  tooltip={offering.tooltip?.replace('$DEPOSIT_AMOUNT$', depositAmount.toString())}
                  value={offering.value}
                />
              );
            })}
          <hr className='col-span-2 row-start-2 @2xl:hidden h-[1px] w-full text-neutrals-300' />
        </ul>
      </div>

      {/* Divider */}
      <hr className='@2xl:block hidden h-[1px] w-full text-neutrals-300' />

      {/* Actions */}
      <Accordion.Root
        className='contents'
        collapsible
        type='single'
      >
        <Accordion.Item
          className='contents'
          value='item-1'
        >
          <div className='flex w-full @2xl:flex-col flex-col items-center @2xl:justify-end font-graphik-condensed'>
            <div className='@2xl:flex contents w-full @2xl:flex-row'>
              {/* Savings */}
              <RatesSavings
                className='order-0 @2xl:block hidden'
                institution={institution}
                isCd={isCd}
                product={product}
              />

              {/* Buttons */}
              <div className='flex w-full @2xl:flex-row flex-col items-end justify-end @2xl:gap-4 gap-6 self-end'>
                {/* Offer Details */}
                <div className='@2xl:order-1 flex @2xl:w-fit w-full flex-col gap-4 @2xl:self-start'>
                  <Button
                    className={cn('w-full bg-black', !newUrl && 'hidden')}
                    variant='primary'
                  >
                    Open Account
                  </Button>
                  {institution.reviews.short_summary && (
                    <button
                      className='flex justify-center'
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onKeyDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (['Enter', ' '].includes(e.key)) {
                          // No action needed here, just to prevent default behavior
                        }
                      }}
                      type='button'
                    >
                      <Accordion.Trigger className='flex items-center self-center text-accent-primary hover:text-accent-primary'>
                        <span className='typography-heading-graphik-condensed-level-6 font-graphik-condensed uppercase hover:underline active:underline'>
                          Offer Details
                        </span>
                        <span className='icon icon-arrow-down-menu group-data-[state=closed]:block group-data-[state=open]:hidden' />
                        <span className='icon icon-arrow-up-menu group-data-[state=open]:block group-data-[state=closed]:hidden' />
                      </Accordion.Trigger>
                    </button>
                  )}
                  <Accordion.Content
                    asChild
                    className='typography-body-graphik-medium @2xl:hidden w-full font-semibold'
                  >
                    {quickLook}
                  </Accordion.Content>
                </div>

                {/* Promoted Offer */}
                <div className='flex @2xl:w-fit w-full flex-col @2xl:items-end gap-2 self-start'>
                  {is_paid && <Button className='w-full bg-warning'>Promoted Offer</Button>}
                  <div className='typography-body-graphik-small max-w-[180px] self-center text-center @2xl:text-end'>
                    {advertiser_notes}
                  </div>
                </div>
              </div>
            </div>
            <Accordion.Content
              asChild
              className='typography-body-graphik-medium order-3 col-span-2 @2xl:block hidden w-full font-graphik-condensed font-semibold'
            >
              {quickLook}
            </Accordion.Content>
          </div>
        </Accordion.Item>
      </Accordion.Root>
    </div>
  );
};
